import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  ArrowPathIcon,
  ArrowDownTrayIcon,
  BeakerIcon,
  ChevronDownIcon,
  ChevronRightIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import axios from '@utils/axios';
import DropdownMenu from './DropdownMenu';

// Every column the panel records, in the order the CSV ships them. The label
// is what a spreadsheet header says; the key is the field on the record.
const EXPORT_COLUMNS = [
  { key: 'createdAt', label: 'Date/Time' },
  { key: 'sensor', label: 'Sensor' },
  { key: 'product', label: 'Product' },
  { key: 'calibrationMethod', label: 'Method' },
  { key: 'operatorMoistureSample', label: 'Actual Moisture (%)' },
  { key: 'operatorDensityKgPerHectoliter', label: 'Actual Density (kg/hL)' },
  { key: 'dmMoistureSample', label: 'Dryer Master Moisture (%)' },
  { key: 'dmMoistureVoltage', label: 'Moisture Voltage (V)' },
  { key: 'dmTemperatureSample', label: 'Temperature (C)' },
  { key: 'oldMoistureBias', label: 'Old Moisture Offset' },
  { key: 'oldMoistureSlope', label: 'Old Moisture Slope' },
  { key: 'oldProductDensity', label: 'Old Product Density' },
  { key: 'oldTemperatureCoefficient', label: 'Old Temperature Coefficient' },
  { key: 'oldDensityCoefficient', label: 'Old Density Coefficient' },
  { key: 'appliedMoistureBias', label: 'Applied Moisture Offset' },
  { key: 'appliedMoistureSlope', label: 'Applied Moisture Slope' },
  { key: 'appliedProductDensity', label: 'Applied Product Density' },
  { key: 'appliedTemperatureCoefficient', label: 'Applied Temperature Coefficient' },
  { key: 'appliedDensityCoefficient', label: 'Applied Density Coefficient' },
  { key: 'fitMoistureBias', label: 'Fit Moisture Offset' },
  { key: 'fitMoistureSlope', label: 'Fit Moisture Slope' },
  { key: 'fitTemperatureCoefficient', label: 'Fit Temperature Coefficient' },
  { key: 'fitDensityCoefficient', label: 'Fit Density Coefficient' },
  { key: 'fitRSquared', label: 'Fit R Squared' },
  { key: 'temperatureBias', label: 'Temperature Offset' },
  { key: 'temperatureSlope', label: 'Temperature Slope' },
  { key: 'panelRecordId', label: 'Panel Record ID' }
];

// The coefficient pairs the expanded row lays out side by side, so what a
// calibration changed can be read straight off.
const COEFFICIENT_ROWS = [
  { label: 'Moisture Offset', old: 'oldMoistureBias', applied: 'appliedMoistureBias' },
  { label: 'Moisture Slope', old: 'oldMoistureSlope', applied: 'appliedMoistureSlope' },
  { label: 'Product Density', old: 'oldProductDensity', applied: 'appliedProductDensity' },
  { label: 'Temperature Coefficient', old: 'oldTemperatureCoefficient', applied: 'appliedTemperatureCoefficient' },
  { label: 'Density Coefficient', old: 'oldDensityCoefficient', applied: 'appliedDensityCoefficient' }
];

const FIT_ROWS = [
  { label: 'Fitted Moisture Offset', key: 'fitMoistureBias' },
  { label: 'Fitted Moisture Slope', key: 'fitMoistureSlope' },
  { label: 'Fitted Temperature Coefficient', key: 'fitTemperatureCoefficient' },
  { label: 'Fitted Density Coefficient', key: 'fitDensityCoefficient' }
];

const METHOD_LABELS = {
  regression: 'Regression',
  single_point: 'Single Point'
};

const isMissing = (value) => value === null || value === undefined;

// Panel record numbers restart at 1 after a factory reset, so in this
// newest-first list a reset sits wherever the next row down carries a higher
// record number than the row above it. The before/after chain does not run
// across that line.
const isResetBoundary = (rows, index) => {
  const next = rows[index + 1];
  return Boolean(next && next.panelRecordId > rows[index].panelRecordId);
};

const fmt = (value, digits = 2) =>
  isMissing(value) ? '—' : Number(value).toFixed(digits);

// Coefficients span several orders of magnitude, so a fixed number of decimals
// either loses the small ones or drowns the large ones in zeros.
const fmtCoefficient = (value) => {
  if (isMissing(value)) return '—';
  const magnitude = Math.abs(Number(value));
  if (magnitude !== 0 && magnitude < 0.001) return Number(value).toExponential(3);
  return Number(value).toFixed(6);
};

const CalibrationHistory = ({ deviceId }) => {
  const [data, setData] = useState(null);
  const [selectedSensor, setSelectedSensor] = useState('all');
  const [expandedRow, setExpandedRow] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`/v1/history/${deviceId}/calibration`);
      setData(response.data.data);
      setError(null);
    } catch (err) {
      console.error('Error fetching calibration history:', err);
      setError(err.response?.data?.message || err.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [deviceId]);

  useEffect(() => {
    setLoading(true);
    fetchData();
  }, [fetchData]);

  const handleRefresh = () => {
    setRefreshing(true);
    fetchData();
  };

  const records = useMemo(() => {
    const all = data?.records || [];
    return selectedSensor === 'all'
      ? all
      : all.filter((record) => record.sensor === selectedSensor);
  }, [data, selectedSensor]);

  // The export carries what is on screen: whatever the sensor filter has
  // narrowed it to, every column, newest first.
  const exportHistory = (exportFormat) => {
    const stamp = format(new Date(), 'yyyy-MM-dd_HH-mm-ss');
    const suffix = selectedSensor === 'all' ? '' : `_${selectedSensor}`;
    let blob;
    let filename;

    if (exportFormat === 'csv') {
      const cell = (record, column) => {
        const value = record[column.key];
        if (isMissing(value)) return '';
        if (column.key === 'createdAt') return format(new Date(value), 'yyyy-MM-dd HH:mm:ss');
        if (typeof value === 'string') return `"${value.replace(/"/g, '""')}"`;
        return value;
      };

      const content = [
        EXPORT_COLUMNS.map((column) => column.label).join(','),
        ...records.map((record) => EXPORT_COLUMNS.map((column) => cell(record, column)).join(','))
      ].join('\n');

      blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
      filename = `calibration_history_${deviceId}${suffix}_${stamp}.csv`;
    } else {
      blob = new Blob([JSON.stringify(records, null, 2)], { type: 'application/json' });
      filename = `calibration_history_${deviceId}${suffix}_${stamp}.json`;
    }

    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="flex justify-between">
          <div className="h-8 w-64 rounded bg-gray-200" />
          <div className="h-8 w-24 rounded bg-gray-200" />
        </div>
        <div className="h-64 rounded-lg bg-gray-100" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-3 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0" />
        <div>
          <p className="font-medium">Failed to load calibration history</p>
          <p className="text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data?.records?.length) {
    return (
      <div className="py-12 text-center text-gray-500">
        <BeakerIcon className="mx-auto mb-3 h-12 w-12 text-gray-300" />
        <p className="font-medium">No calibrations recorded</p>
      </div>
    );
  }

  const totalRows = data.records.length;

  return (
    <div className="space-y-4">
      {/* Controls */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => setSelectedSensor('all')}
            className={`rounded-full px-3 py-1.5 text-sm font-medium transition-colors ${
              selectedSensor === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All sensors
            <span className={`ml-1.5 text-xs ${
              selectedSensor === 'all' ? 'text-blue-200' : 'text-gray-400'
            }`}>
              {totalRows}
            </span>
          </button>
          {data.sensors.map((entry) => (
            <button
              key={entry.sensor}
              onClick={() => setSelectedSensor(entry.sensor)}
              className={`rounded-full px-3 py-1.5 text-sm font-medium capitalize transition-colors ${
                entry.sensor === selectedSensor
                  ? 'bg-blue-600 text-white'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              {entry.sensor}
              <span className={`ml-1.5 text-xs ${
                entry.sensor === selectedSensor ? 'text-blue-200' : 'text-gray-400'
              }`}>
                {entry.count}
              </span>
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu
            trigger={({ toggle }) => (
              <button
                onClick={toggle}
                className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-200"
                title="Download calibration history"
              >
                <ArrowDownTrayIcon className="h-4 w-4" />
                Download
              </button>
            )}
          >
            <button
              onClick={() => exportHistory('csv')}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              Download as CSV
            </button>
            <button
              onClick={() => exportHistory('json')}
              className="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
            >
              Download as JSON
            </button>
          </DropdownMenu>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-sm text-gray-600 transition-colors hover:bg-gray-200 disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="w-8 px-3 py-2" />
              <th className="px-3 py-2 text-left font-medium text-gray-600">Date/Time</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Sensor</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Product</th>
              <th className="px-3 py-2 text-left font-medium text-gray-600">Method</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Operator</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Dryer Master</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">Difference</th>
              <th className="px-3 py-2 text-right font-medium text-gray-600">R²</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {records.map((record, index) => {
              const expanded = expandedRow === record.syncId;
              const difference = isMissing(record.operatorMoistureSample) || isMissing(record.dmMoistureSample)
                ? null
                : Number(record.operatorMoistureSample) - Number(record.dmMoistureSample);

              return (
                <React.Fragment key={record.syncId}>
                  <tr
                    onClick={() => setExpandedRow(expanded ? null : record.syncId)}
                    className="cursor-pointer hover:bg-gray-50"
                  >
                    <td className="px-3 py-2 text-gray-400">
                      {expanded
                        ? <ChevronDownIcon className="h-4 w-4" />
                        : <ChevronRightIcon className="h-4 w-4" />}
                    </td>
                    <td className="whitespace-nowrap px-3 py-2 text-gray-900">
                      {format(new Date(record.createdAt), 'MMM dd, yyyy HH:mm:ss')}
                    </td>
                    <td className="px-3 py-2 capitalize text-gray-700">{record.sensor}</td>
                    <td className="px-3 py-2 capitalize text-gray-700">{record.product}</td>
                    <td className="px-3 py-2">
                      <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        record.calibrationMethod === 'regression'
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {METHOD_LABELS[record.calibrationMethod] || record.calibrationMethod}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right text-gray-900">{fmt(record.operatorMoistureSample)}%</td>
                    <td className="px-3 py-2 text-right text-gray-900">{fmt(record.dmMoistureSample)}%</td>
                    <td className={`px-3 py-2 text-right font-medium ${
                      difference === null ? 'text-gray-400'
                        : Math.abs(difference) >= 1 ? 'text-amber-600'
                        : 'text-gray-700'
                    }`}>
                      {difference === null ? '—' : `${difference > 0 ? '+' : ''}${difference.toFixed(2)}`}
                    </td>
                    <td className="px-3 py-2 text-right text-gray-700">
                      {isMissing(record.fitRSquared) ? '—' : Number(record.fitRSquared).toFixed(4)}
                    </td>
                  </tr>

                  {expanded && (
                    <tr className="bg-gray-50">
                      <td colSpan={9} className="px-6 py-4">
                        <div className="grid gap-6 md:grid-cols-3">
                          <div>
                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Reading at calibration
                            </h4>
                            <dl className="space-y-1 text-sm">
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Actual Moisture</dt>
                                <dd className="text-gray-900">{fmt(record.operatorMoistureSample)}%</dd>
                              </div>
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Actual Density</dt>
                                <dd className="text-gray-900">{fmt(record.operatorDensityKgPerHectoliter, 3)} kg/hL</dd>
                              </div>
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Dryer Master Moisture</dt>
                                <dd className="text-gray-900">{fmt(record.dmMoistureSample)}%</dd>
                              </div>
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Moisture Voltage</dt>
                                <dd className="text-gray-900">{fmt(record.dmMoistureVoltage, 5)} V</dd>
                              </div>
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Temperature</dt>
                                <dd className="text-gray-900">{fmt(record.dmTemperatureSample, 1)} °C</dd>
                              </div>
                            </dl>
                          </div>

                          <div>
                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Coefficients before → after
                            </h4>
                            <dl className="space-y-1 text-sm">
                              {COEFFICIENT_ROWS.map((row) => {
                                const changed = record[row.old] !== record[row.applied];
                                return (
                                  <div key={row.label} className="flex justify-between gap-4">
                                    <dt className="text-gray-600">{row.label}</dt>
                                    <dd className={changed ? 'font-medium text-gray-900' : 'text-gray-400'}>
                                      {fmtCoefficient(record[row.old])}
                                      <span className="mx-1 text-gray-400">→</span>
                                      {fmtCoefficient(record[row.applied])}
                                    </dd>
                                  </div>
                                );
                              })}
                            </dl>
                          </div>

                          <div>
                            <h4 className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-500">
                              Regression fit
                            </h4>
                            {record.calibrationMethod === 'regression' ? (
                              <dl className="space-y-1 text-sm">
                                {FIT_ROWS.map((row) => (
                                  <div key={row.key} className="flex justify-between gap-4">
                                    <dt className="text-gray-600">{row.label}</dt>
                                    <dd className="text-gray-900">{fmtCoefficient(record[row.key])}</dd>
                                  </div>
                                ))}
                                <div className="flex justify-between gap-4">
                                  <dt className="text-gray-600">R²</dt>
                                  <dd className="text-gray-900">
                                    {isMissing(record.fitRSquared) ? '—' : Number(record.fitRSquared).toFixed(4)}
                                  </dd>
                                </div>
                              </dl>
                            ) : (
                              <p className="text-sm text-gray-400">—</p>
                            )}
                            <dl className="mt-3 space-y-1 border-t border-gray-200 pt-3 text-sm">
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Temperature Offset</dt>
                                <dd className="text-gray-900">{fmtCoefficient(record.temperatureBias)}</dd>
                              </div>
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Temperature Slope</dt>
                                <dd className="text-gray-900">{fmtCoefficient(record.temperatureSlope)}</dd>
                              </div>
                              <div className="flex justify-between gap-4">
                                <dt className="text-gray-600">Panel Record</dt>
                                <dd className="text-gray-900">#{record.panelRecordId}</dd>
                              </div>
                            </dl>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}

                  {isResetBoundary(records, index) && (
                    <tr className="bg-amber-50">
                      <td colSpan={9} className="px-3 py-2 text-center text-xs font-medium text-amber-800">
                        Panel factory reset
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex items-start gap-2 rounded-lg bg-blue-50 px-3 py-2 text-xs text-gray-600">
        <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-blue-500" />
        <span>
          {records.length} of {totalRows} calibrations. Temperatures are Celsius.
        </span>
      </div>
    </div>
  );
};

export default CalibrationHistory;
